<script setup lang="ts">
import type { ArticleContent } from '~/models/Content/ArticleContent'
import type { ButtonShades } from '~/components/ui/Button.vue'
import { computed } from 'vue'
import { faQuestion } from '@fortawesome/duotone-regular-svg-icons'
import { firstOf } from '~/utils/queryUtils'
import { useMedia } from '~/composables/useMedia'
import { useIcons } from '~/composables/useIcons'
import RichTextRenderer from '~/components/utils/RichTextRenderer.vue'
import FullscreenContent from '~/components/utils/FullscreenContent.vue'
import CopyrightButton from '~/components/utils/CopyrightButton.vue'
import AuSkeleton from '~/components/ui/AuSkeleton.vue'
import Image from '~/components/media/Image.vue'

const { shade = 'light', articleContent, dashed = false } = defineProps<{
  articleContent: ArticleContent
  dashed?: boolean
  shade?: ButtonShades
}>()

const { duotone, iconByContentId } = useIcons()
const mediaContentId = computed(() => [Number(articleContent.svg?.destinationContentId ?? articleContent.image?.destinationContentId ?? 0)])
const { data: media, isFetching } = firstOf(useMedia(mediaContentId))
const reverse = computed(() => articleContent.reverse)
const icon = iconByContentId(Number(articleContent.icon?.destinationContentId), duotone)

const isLight = computed(() => shade === 'light')

const bgColor = computed(() => {
  return isLight.value
    ? 'bg-(--theme-10)'
    : 'bg-(--theme-60)'
})

const textColor = computed(() => {
  return isLight.value
    ? 'text-(--theme-60)'
    : 'text-(--theme-pop)'
})

const border = computed(() => {
  if (!dashed)
    return

  return isLight.value
    ? 'border-2 border-dashed border-(--theme-60)'
    : 'border-2 border-dashed border-(--theme-pop)'
})

const iconStyles = computed(() => ({
  '--fa-primary-color': isLight.value ? 'var(--theme-60)' : 'var(--theme-pop)',
  '--fa-secondary-color': isLight.value ? 'var(--theme-pop)' : 'var(--theme-20)',
  '--fa-secondary-opacity': '1',
}))
</script>

<template>
  <section :class="media ? 'col-[media]' : 'col-[text]'">
    <div
      class="xs:items-center flex gap-(--col-gap) rounded-xl"
      :class="[
        bgColor, textColor, border,
        { 'xs:flex-row-reverse': reverse,
          'xs:flex-row flex-col p-[calc(var(--col-gap)+var(--col-width))] py-(--col-width)': media,
          'p-4 md:-mx-(--col-gap) lg:gap-8 lg:p-8': !media,
        }]"
    >
      <AuSkeleton
        v-if="isFetching"
        class="flex-none"
        height="300px"
      />
      <div
        v-else-if="media"
        class="flex-1"
      >
        <FullscreenContent :bg-class="bgColor">
          <template #default="{ isFullscreen }">
            <Image
              :content="media"
              class="col-start-1 row-start-1 max-h-[90lvh] !w-full"
              :class="[
                isFullscreen && bgColor,
                {
                  'place-self-center object-contain p-4': isFullscreen,
                  'object-cover': !isFullscreen,
                }]"
              :width="isFullscreen ? '100vw' : '400px'"
            />

            <CopyrightButton
              v-if="media && 'copyright' in media && media.copyright"
              class="col-start-1 row-start-1 place-self-end"
              :copyright="media.copyright"
              color-theme
            />

            <div
              v-if="media && 'caption' in media && media.caption"
              class="row-start-2 flex flex-col place-self-stretch bg-white p-4 text-black"
            >
              <RichTextRenderer :text="media.caption" />
            </div>
          </template>
        </FullscreenContent>
      </div>

      <div
        v-if="articleContent.icon?.destinationContentId && !media"
        class="grid place-self-start text-lg"
      >
        <font-awesome-icon
          :icon="icon || faQuestion"
          size="2x"
          :style="iconStyles"
        />
      </div>

      <div class="au-prose flex-1 self-center">
        <RichTextRenderer :text="articleContent.body ?? ''" />
      </div>
    </div>
  </section>
</template>
